import {delay} from '@yandex-delivery/utils'

import type {ConfigTypeFromFetchFunction, FetchFunction} from '../types'
import {ServerError} from '../ServerError'

type RetryFunction = (attempt: number) => number
type RetryOptions = {
  retries: number
  delay: number | RetryFunction
  /**
   * Функция для определения, нужно ли ретраить запрос с такой ошибкой
   * @param err  Ошибка из фетча
   * @default () => true
   */
  isRetriable?: (err: Error) => boolean
}

const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  retries: 2,
  delay: 100,
  isRetriable: () => true,
}

export function createRetryMiddleware(options: Partial<RetryOptions>) {
  const _options = {...DEFAULT_RETRY_OPTIONS, ...options}

  return function withRetry<TReq = unknown, TRes = unknown, TExtraConfig extends Record<string, unknown> = {}>(
    fetchFunc: FetchFunction<TReq, TRes, TExtraConfig>,
  ) {
    return function enhancedFetch(config: ConfigTypeFromFetchFunction<typeof fetchFunc> & Partial<RetryOptions>) {
      const retries = config.retries ?? _options.retries
      const delayMs = config.delay ?? _options.delay
      const isRetriable = config.isRetriable ?? _options.isRetriable
      const delayFn: RetryFunction = typeof delayMs === 'function' ? delayMs : () => delayMs

      const retry = (attempt: number = 0): ReturnType<FetchFunction<TReq, TRes, TExtraConfig>> => {
        if (attempt > 0 && config.signal) {
          // важно обновить сигнал, если он был до этого
          // иначе из-за delay, fetch будет отваливаться по таймауту
          // P.S. 1.5 взято с потолка
          config.signal = AbortSignal.timeout(delayFn(attempt) * 1.5)
        }

        return fetchFunc(config).catch(err => {
          if (attempt < retries) {
            const timeout = delayFn(attempt)
            config.logger?.debug(
              {
                err,
                body: {
                  attempt,
                  timeout,
                },
                req: {
                  url: config.url,
                  method: config.method,
                  headers: config.headers,
                  body: config.body,
                },
              },
              'attempt to retry',
            )

            if (!isRetriable(err)) {
              throw err
            }

            return delay(timeout).then(() => retry(attempt + 1))
          }

          if (err instanceof ServerError) {
            throw err
          }

          throw new ServerError({
            url: config.url,
            body: {
              code: 'abort_after_retry',
              message: err.message,
              attempt,
            },
            headers: config.headers,
            status: 500,
            error: err,
          })
        })
      }

      return retry()
    }
  }
}
